import React, { useEffect, useId, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import BackofficeReportsApi from './backofficeReportsApi';

const REFRESH_MS = 5 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 30 * 1000;
const SIGNAL_COLORS = {
  offline: { dot: '#d32f2f', text: '#9f1239', background: '#fff1f2', border: '#fecdd3', halo: 'rgba(211,47,47,0.10)' },
  noRecentOrders: { dot: '#ed6c02', text: '#9a3412', background: '#fff7ed', border: '#fed7aa', halo: 'rgba(237,108,2,0.12)' },
  orderAttention: { dot: '#7b1fa2', text: '#6b21a8', background: '#faf5ff', border: '#e9d5ff', halo: 'rgba(123,31,162,0.12)' },
  deliveryAttention: { dot: '#1976d2', text: '#1d4ed8', background: '#eff6ff', border: '#bfdbfe', halo: 'rgba(25,118,210,0.12)' },
};
const ORDER_REASON_LABELS = {
  AWAITING_ACCEPTANCE: 'Awaiting acceptance',
  AWAITING_COMPLETION: 'Awaiting completion',
};
const DELIVERY_REASON_LABELS = {
  NO_COURIER: 'No courier',
  NOT_PICKED_UP: 'Not picked up',
  NOT_DELIVERED: 'Not delivered',
  DELIVERY_FAILED: 'Delivery failed',
};
const stampFormatter = new Intl.DateTimeFormat('ro-RO', {
  timeZone: 'Europe/Bucharest', day: '2-digit', month: '2-digit',
  hour: '2-digit', minute: '2-digit', hour12: false,
});

function validStamp(value) {
  return typeof value === 'string' && Number.isFinite(Date.parse(value));
}

function validResponse(data) {
  return data && validStamp(data.checkedAt)
    && ['offline', 'noRecentOrders'].every((key) => Array.isArray(data[key])
      && data[key].every((pos) => pos && typeof pos.iref === 'string'
        && typeof pos.label === 'string' && typeof pos.name === 'string'
        && (pos.lastSeenOnline === null || validStamp(pos.lastSeenOnline))
        && (pos.lastOrderCreatedOn === null || validStamp(pos.lastOrderCreatedOn))
        && (pos.lastOrderReceivedOn == null || validStamp(pos.lastOrderReceivedOn))))
    && (data.orderWaitMinutes === undefined || data.orderWaitMinutes === null
      || (Number.isInteger(data.orderWaitMinutes) && data.orderWaitMinutes > 0))
    && (data.orderAttention === undefined || data.orderAttention === null
      || (Array.isArray(data.orderAttention)
        && data.orderAttention.every((order) => order
          && (typeof order.iref === 'number' || typeof order.iref === 'string')
          && typeof order.code === 'string'
          && typeof order.locationIref === 'string'
          && typeof order.locationName === 'string'
          && Object.prototype.hasOwnProperty.call(ORDER_REASON_LABELS, order.reason)
          && Number.isInteger(order.elapsedMinutes)
          && order.elapsedMinutes >= 0)))
    && (data.deliveryAttention === undefined
      || (Array.isArray(data.deliveryAttention)
        && data.deliveryAttention.every((order) => order
          && (typeof order.iref === 'number' || typeof order.iref === 'string')
          && typeof order.code === 'string'
          && typeof order.locationIref === 'string'
          && typeof order.locationName === 'string'
          && Object.prototype.hasOwnProperty.call(DELIVERY_REASON_LABELS, order.reason)
          && Number.isInteger(order.elapsedMinutes)
          && order.elapsedMinutes >= 0)));
}

function formatStamp(value) {
  return value ? stampFormatter.format(new Date(value)) : 'Never recorded';
}

function formatElapsed(minutes) {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return `${hours} h${remainder ? ` ${remainder} min` : ''}`;
}

function getShortOrderNumber(code, iref) {
  const trailingDigits = String(code || '').match(/(\d+)$/)?.[1];
  return trailingDigits ? trailingDigits.slice(-4) : String(iref ?? '—');
}

function SignalGroup({ title, rows, kind, expanded, onToggle }) {
  const panelId = useId();
  const colors = SIGNAL_COLORS[kind];
  const delivery = kind === 'deliveryAttention';
  const orderAttention = kind === 'orderAttention';
  if (!rows.length) return null;
  return (
    <Accordion disableGutters elevation={0} expanded={expanded} onChange={(_, value) => onToggle(value)}
      TransitionProps={{ unmountOnExit: true }}
      sx={{ border: `1px solid ${colors.border}`, borderRadius: '10px !important', overflow: 'hidden',
        '&:before': { display: 'none' }, '& + .MuiAccordion-root': { mt: 1 } }}>
      <AccordionSummary id={`${panelId}-header`} aria-controls={`${panelId}-content`}
        aria-label={`${title} (${rows.length})`}
        expandIcon={<ExpandMoreRoundedIcon sx={{ color: colors.text, fontSize: 20 }} />}
        sx={{ px: 1.25, minHeight: 44, bgcolor: colors.background,
          '& .MuiAccordionSummary-content': { my: 0.75, gap: 1, alignItems: 'center' },
          '&.Mui-focusVisible': { outline: `2px solid ${colors.text}`, outlineOffset: -2 } }}>
        <Typography component="h3" sx={{ flex: 1, fontSize: 13, fontWeight: 800, color: colors.text }}>
          {title}
        </Typography>
        <Box component="span" sx={{ minWidth: 24, px: 0.75, py: 0.15, mr: 0.75, textAlign: 'center', borderRadius: '6px', bgcolor: colors.text, color: '#fff', fontSize: 12, fontWeight: 800 }}>
          {rows.length}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.25, py: 0.25 }}>
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {rows.map((row) => (
            <Box component="li" key={row.iref} sx={{ px: 0.5, py: 1.15, '& + &': { borderTop: '1px solid #e2e8f0' } }}>
              {delivery || orderAttention ? (
                <>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflowWrap: 'anywhere' }}>
                    {row.locationName || row.locationIref} · Order {getShortOrderNumber(row.code, row.iref)} · #{row.iref}
                  </Typography>
                  <Typography sx={{ mt: 0.25, fontSize: 12, fontWeight: row.reason === 'DELIVERY_FAILED' ? 700 : 400,
                    color: row.reason === 'DELIVERY_FAILED' ? '#b91c1c' : '#64748b' }}>
                    {orderAttention ? ORDER_REASON_LABELS[row.reason] : DELIVERY_REASON_LABELS[row.reason]}
                    {(orderAttention || row.reason !== 'DELIVERY_FAILED') ? ` · ${formatElapsed(row.elapsedMinutes)}` : ''}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflowWrap: 'anywhere' }}>
                    {row.label || row.name || row.iref}
                    {row.label && row.name && row.label !== row.name ? ` · ${row.name}` : ''}
                  </Typography>
                  <Typography sx={{ mt: 0.25, fontSize: 12, color: '#64748b' }}>
                    Last connection: {formatStamp(row.lastSeenOnline)}
                  </Typography>
                </>
              )}
              {kind === 'noRecentOrders' && (
                <Typography sx={{ mt: 0.25, fontSize: 12, color: '#64748b' }}>
                  {row.lastOrderReceivedOn ? 'Last order received: ' : 'Last order: '}
                  {formatStamp(row.lastOrderReceivedOn || row.lastOrderCreatedOn)}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

export default function POSSignals({ client, locationGroup = 'all' }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const cardRef = useRef(null);
  const refreshRef = useRef(() => {});
  const popoverId = useId();

  useEffect(() => {
    let active = true;
    let pending = false;
    let forbidden = false;
    let request;
    let requestTimer;
    let lastCheck = 0;
    setData(null);
    setError(false);
    setAnchorEl(null);
    setExpandedGroups({});

    const refresh = async () => {
      if (!active || pending || forbidden || document.hidden) return;
      pending = true;
      setLoading(true);
      try {
        request = BackofficeReportsApi.getPosSignalsAsync(client, { locationGroup });
        const timeout = new Promise((_, reject) => {
          requestTimer = setTimeout(() => {
            reject(new Error('POS signal request timed out.'));
            request?.abort?.();
          }, REQUEST_TIMEOUT_MS);
        });
        const response = await Promise.race([Promise.resolve(request), timeout]);
        if (!validResponse(response)) throw new Error('Invalid POS signal response.');
        if (!active) return;
        setData(response);
        setError(false);
        lastCheck = Date.now();
        if (!response.offline.length && !response.noRecentOrders.length
          && !(response.deliveryAttention || []).length
          && !(response.orderAttention || []).length
          && response.orderAttention !== null) setAnchorEl(null);
      } catch (failure) {
        if (!active) return;
        if ([401, 403].includes(failure?.status)) {
          forbidden = true;
          setData(null);
          setError(false);
          setAnchorEl(null);
        } else {
          // Keep the last successful result visibly stale; failure is not zero.
          setError(true);
        }
      } finally {
        clearTimeout(requestTimer);
        pending = false;
        request = null;
        if (active) setLoading(false);
      }
    };

    const onVisibilityChange = () => {
      if (!document.hidden && Date.now() - lastCheck >= REFRESH_MS) refresh();
    };
    refreshRef.current = refresh;
    refresh();
    const poll = setInterval(refresh, REFRESH_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      active = false;
      clearInterval(poll);
      clearTimeout(requestTimer);
      request?.abort?.();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [client, locationGroup]);

  const offline = data?.offline || [];
  const noOrders = data?.noRecentOrders || [];
  const deliveryAttention = data?.deliveryAttention || [];
  const orderAttention = data?.orderAttention || [];
  const orderAttentionUnavailable = data?.orderAttention === null;
  if (!offline.length && !noOrders.length && !deliveryAttention.length
    && !orderAttention.length && !orderAttentionUnavailable && !error) return null;

  const openDetails = (category) => {
    setExpandedGroups(category ? { [category]: true } : {});
    setAnchorEl(cardRef.current);
    refreshRef.current();
  };

  return (
    <Box sx={{ display: 'flex', minWidth: 0, gridColumn: { xs: '1 / -1', lg: 5 }, gridRow: { lg: 1 } }}>
      <Card
        component="button"
        type="button"
        ref={cardRef}
        onClick={() => openDetails()}
        aria-label="Show POS signals"
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchorEl)}
        aria-controls={anchorEl ? popoverId : undefined}
        sx={{
          width: { xs: '100%', lg: 144 }, minHeight: 48,
          display: 'flex', alignItems: { xs: 'center', lg: 'flex-start' }, flexDirection: { xs: 'row', lg: 'column' },
          justifyContent: 'space-between', gap: { xs: 2, lg: 1.5 }, textAlign: 'left',
          px: { xs: 1.5, sm: 1.75, md: 2 }, py: { xs: 1, lg: 2 }, color: '#1a237e', position: 'relative', overflow: 'hidden',
          background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
          border: '1px solid rgba(15,23,42,0.10)', borderRadius: { xs: '14px', sm: '16px' },
          boxShadow: '0 4px 14px rgba(15,23,42,0.08)', cursor: 'pointer', font: 'inherit',
          '&:before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 4, bgcolor: '#1a237e' },
          '&:hover': { background: '#f1f3fc', borderColor: 'rgba(26,35,126,0.3)' },
          '&:focus-visible': { outline: '2px solid #1a237e', outlineOffset: 2 },
        }}
      >
        <Typography component="span" sx={{ fontSize: { xs: '0.74rem', sm: '0.8rem', md: '0.84rem' }, lineHeight: 1.2, fontWeight: 800, letterSpacing: '.015em', textTransform: 'uppercase' }}>
          POS signals
          {error && <Box component="span" sx={{ display: 'block', mt: 0.35, fontSize: 11, fontWeight: 500, textTransform: 'none', color: '#64748b' }}>{data ? 'outdated' : 'unavailable'}</Box>}
          {!error && orderAttentionUnavailable && <Box component="span" sx={{ display: 'block', mt: 0.35, fontSize: 11, fontWeight: 500, textTransform: 'none', color: '#6b21a8' }}>order wait unavailable</Box>}
        </Typography>
        <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
          {[
            { key: 'offline', label: 'Offline POS', count: offline.length },
            { key: 'noRecentOrders', label: 'POS with no recent orders', count: noOrders.length },
            { key: 'orderAttention', label: 'Order attention', count: orderAttention.length },
            { key: 'deliveryAttention', label: 'Delivery attention', count: deliveryAttention.length },
          ].filter((group) => group.count > 0).map((group) => (
            <Tooltip key={group.label} title={group.label}>
              <Box component="span" role="img" aria-label={group.label}
                onClick={(event) => { event.stopPropagation(); openDetails(group.key); }}
                sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 32 }}>
                <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: SIGNAL_COLORS[group.key].dot,
                  boxShadow: `0 0 0 4px ${SIGNAL_COLORS[group.key].halo}`, opacity: error ? 0.5 : 1 }} />
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Card>

      <Popover
        open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }} marginThreshold={12}
        PaperProps={{
          id: popoverId, role: 'dialog', 'aria-label': 'POS signals',
          sx: { width: 340, maxWidth: 'calc(100vw - 24px)', height: 480, maxHeight: 'calc(100vh - 32px)',
            display: 'flex', flexDirection: 'column', borderRadius: '16px', mt: 0.75,
            border: '1px solid #e2e8f0', boxShadow: '0 12px 36px rgba(15,23,42,0.14)', overflow: 'hidden' },
        }}
      >
        <Box sx={{ display: 'flex', flexShrink: 0, alignItems: 'center', justifyContent: 'space-between', pl: 2, pr: 0.5, py: 0.5,
          bgcolor: '#f1f3fc', borderTop: '4px solid #1a237e', borderBottom: '1px solid #dbe0f2' }}>
          <Typography component="h2" sx={{ fontSize: 14, fontWeight: 800, color: '#1a237e' }}>POS signals</Typography>
          <IconButton aria-label="Close POS signals" onClick={() => setAnchorEl(null)} sx={{ width: 40, height: 40, color: '#64748b' }}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ p: 2, flex: 1, minHeight: 0, overflowY: 'auto', scrollbarGutter: 'stable' }}>
          {error && <Typography role="status" sx={{ mb: data ? 1.5 : 0, fontSize: 12, color: '#64748b' }}>Could not update POS status.{data ? ' Showing the last successful check.' : ''}</Typography>}
          <SignalGroup title="Offline" rows={offline} kind="offline" expanded={Boolean(expandedGroups.offline)}
            onToggle={(value) => setExpandedGroups((current) => ({ ...current, offline: value }))} />
          <SignalGroup title="No recent orders" rows={noOrders} kind="noRecentOrders" expanded={Boolean(expandedGroups.noRecentOrders)}
            onToggle={(value) => setExpandedGroups((current) => ({ ...current, noRecentOrders: value }))} />
          {orderAttentionUnavailable && <Typography role="status" sx={{ mb: 1.5, fontSize: 12, color: '#6b21a8' }}>Order wait signal unavailable. Check the incoming orders window setting.</Typography>}
          <SignalGroup title="Order attention" rows={orderAttention} kind="orderAttention"
            expanded={Boolean(expandedGroups.orderAttention)}
            onToggle={(value) => setExpandedGroups((current) => ({ ...current, orderAttention: value }))} />
          <SignalGroup title="Delivery attention" rows={deliveryAttention} kind="deliveryAttention"
            expanded={Boolean(expandedGroups.deliveryAttention)}
            onToggle={(value) => setExpandedGroups((current) => ({ ...current, deliveryAttention: value }))} />
        </Box>
        <Box sx={{ display: 'flex', flexShrink: 0, alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 2, py: 0.75, bgcolor: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
          <Typography aria-live="polite" sx={{ fontSize: 11, color: '#64748b' }}>
            {loading ? 'Updating…' : data ? `Checked ${formatStamp(data.checkedAt)}` : 'Status unavailable'}
          </Typography>
          <Button size="small" disabled={loading} onClick={() => refreshRef.current()} sx={{ minWidth: 0, color: '#64748b', textTransform: 'none', fontSize: 12 }}>Refresh</Button>
        </Box>
      </Popover>
    </Box>
  );
}
